import {
  RUTERO_CONFIRM_ANSWERS_MAX,
  ruteroAnswerMatchesFormat,
} from '@bandido/shared/types';

import type {
  PendingQuestion,
  PendingQuestionOption,
  QuestionAnswer,
} from './queries';

/**
 * Lo tipeado hasta ahora, por IDENTIDAD de pregunta (ver `questionKey`). Es un
 * draft: mientras no se confirme, nada de esto viajó a ningún lado.
 */
export type QuestionAnswersDraft = Record<string, string>;

/**
 * La identidad de una pregunta: su `code` y, cuando el operador pide el mismo
 * dato una vez POR PASAJERO, el índice de ese pasajero.
 *
 * El código solo NO alcanza: con preguntas por pasajero llegan varias con el
 * mismo `code`, y usarlo de clave hace que las copias compartan el valor —el
 * asesor escribe el documento del primer pasajero, viaja para todos, el
 * operador lo acepta y la reserva queda mal SIN error. Es el modo de falla
 * que motivó #2390.
 *
 * Se serializa como PAR, no se concatena: el contrato acota el largo del
 * `code` pero no prohíbe ningún carácter, así que cualquier separador puede
 * aparecer dentro del código y hacer que una pregunta del booking entero
 * colisione con la copia de un pasajero — la copia se comería el borrador y
 * la clave de React de la otra, y una obligatoria podría desaparecer de la
 * pantalla. Un par serializado no puede colisionar por construcción.
 *
 * Misma regla que usa el servidor al emparejar el confirm
 * (`questionIdentity`): lo que se dibuja y lo que se valida se emparejan
 * igual (§21.22).
 */
export function questionKey(
  question: { code: string; passengerIndex?: number },
): string {
  return JSON.stringify([question.code, question.passengerIndex ?? null]);
}

/**
 * Las preguntas del operador de ESTA reserva: una sola expresión de la que
 * salen la sección que se dibuja, el bloqueo del CTA y las respuestas que
 * viajan en el confirm. Si fueran tres derivaciones parecidas podría quedar
 * una pregunta que bloquea y no se dibuja —un botón gris para siempre— o una
 * respuesta que viaja por una pregunta que la reserva ya no tiene.
 */
export function bookingPendingQuestions(
  booking: { pendingQuestions?: PendingQuestion[] } | null | undefined,
): PendingQuestion[] {
  return booking?.pendingQuestions ?? [];
}

/**
 * A qué pasajero le corresponde esta copia de la pregunta, listo para
 * rotular. `number` es 1-based porque es lo que se le muestra al asesor; el
 * contrato indexa desde 0.
 *
 * `name` solo si el pasajero está cargado y tiene nombre: una copia cuyo
 * índice no corresponde a ningún pasajero se rotula igual con su número —el
 * hub garantiza que no pasa, pero es dato de un tercero y perder la copia
 * sería peor que rotularla a secas.
 *
 * `undefined` = la pregunta es del booking entero y no se rotula.
 */
export function questionPassengerLabel(
  question: { passengerIndex?: number },
  passengers?: { firstName?: string; lastName?: string }[],
): { number: number; name?: string } | undefined {
  const { passengerIndex } = question;
  if (passengerIndex === undefined) return undefined;
  const passenger = passengers?.[passengerIndex];
  const name = [passenger?.firstName, passenger?.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
  return { number: passengerIndex + 1, ...(name ? { name } : {}) };
}

/**
 * `mandatory` ausente se lee como obligatoria: el contrato la declara
 * REQUERIDA en cada pregunta, así que su ausencia es un dato que no llegó, y
 * ante la duda bloquear es preferible a confirmar sin el dato que el operador
 * exige (el confirm terminaría en 400 del hub, con la reserva viva).
 */
export function isQuestionMandatory(question: PendingQuestion): boolean {
  return question.mandatory !== false;
}

/**
 * Las opciones que la pregunta publica, si publica alguna. Una elección SIN
 * lista es un estado del contrato: ahí la respuesta viaja textual.
 */
export function questionOptions(question: PendingQuestion): PendingQuestionOption[] {
  return question.dataType === 'options' && question.options?.length
    ? question.options
    : [];
}

/**
 * Respondida = tiene texto, respeta la FORMA que el operador declaró y, si la
 * pregunta publica opciones, ese texto es el `value` de UNA de ellas.
 *
 * La forma se valida acá y no solo se sugiere con el control, porque el valor
 * viaja al operador TAL COMO SE MANDA —el contrato dice que el hub normaliza
 * la declaración de la forma, no reescribe la respuesta—: un `1e3` en un
 * campo numérico o un `TRUE` heredado de un draft anterior llegarían así al
 * operador y los rechazaría con la reserva ya viva. Mal formada = sigue sin
 * responder: la pregunta bloquea y esa respuesta no viaja.
 */
export function isQuestionAnswered(
  question: PendingQuestion,
  answers: QuestionAnswersDraft,
): boolean {
  const answer = answers[questionKey(question)]?.trim();
  if (!answer) return false;
  if (!ruteroAnswerMatchesFormat(question.dataType, answer)) return false;
  const options = questionOptions(question);
  return options.length === 0 || options.some((option) => option.value === answer);
}

/**
 * Las obligatorias todavía sin responder. Vacío ⇒ confirmar no está bloqueado
 * por preguntas. Las NO obligatorias nunca entran acá: vacías se confirman
 * igual (el operador las publicó como opcionales).
 */
export function unansweredMandatoryQuestions(
  questions: PendingQuestion[],
  answers: QuestionAnswersDraft,
): PendingQuestion[] {
  return questions.filter(
    (question) =>
      isQuestionMandatory(question) && !isQuestionAnswered(question, answers),
  );
}

/**
 * Lo que viaja en `POST /bookings/{id}/confirm`.
 *
 * Sale de las preguntas de la RESERVA, no de lo tipeado: un draft de una
 * pregunta que la reserva ya no publica —o de una opción que ya no está en su
 * lista— no se manda. Y una pregunta sin responder no produce entrada — JAMÁS
 * se autocompleta, porque una respuesta inventada CONFIRMA la reserva con un
 * dato incorrecto (contrato del hub).
 *
 * El confirm admite hasta `RUTERO_CONFIRM_ANSWERS_MAX` respuestas y la lista
 * de preguntas NO tiene tope, así que cuando hay más respondidas que las que
 * entran, las OBLIGATORIAS van primero: son las que deciden si el confirm
 * procede. Lo que queda afuera son opcionales, y la pantalla lo dice — nunca
 * se recorta en silencio (`answersOmittedFromConfirm`).
 */
export function questionAnswerPayload(
  questions: PendingQuestion[],
  answers: QuestionAnswersDraft,
): QuestionAnswer[] {
  const answered = questions.filter((question) =>
    isQuestionAnswered(question, answers),);
  const ordered = [
    ...answered.filter(isQuestionMandatory),
    ...answered.filter((question) => !isQuestionMandatory(question)),
  ];
  return ordered.slice(0, RUTERO_CONFIRM_ANSWERS_MAX).map((question) => ({
    code: question.code,
    value: answers[questionKey(question)].trim(),
    // El índice viaja de vuelta: sin él el operador no sabe de qué pasajero
    // es el dato y lo guarda para todos.
    ...(question.passengerIndex !== undefined
      ? { passengerIndex: question.passengerIndex }
      : {}),
  }));
}

/**
 * Cuántas respuestas tipeadas NO entran en el confirm. Cero es el caso normal;
 * más que cero se le dice al asesor, para que no crea que mandó algo que no
 * mandó.
 */
export function answersOmittedFromConfirm(
  questions: PendingQuestion[],
  answers: QuestionAnswersDraft,
): number {
  const answered = questions.filter((question) =>
    isQuestionAnswered(question, answers),).length;
  return Math.max(0, answered - RUTERO_CONFIRM_ANSWERS_MAX);
}

/**
 * Las obligatorias no entran en un solo confirm: más de las que el contrato
 * admite como respuestas. No hay payload válido posible —responder todas es
 * justamente lo que el hub exige para confirmar—, así que el CTA se bloquea
 * diciendo qué pasa en vez de mandar un pedido que se sabe que falla.
 */
export function mandatoryAnswersExceedConfirmLimit(
  questions: PendingQuestion[],
): boolean {
  return questions.filter(isQuestionMandatory).length > RUTERO_CONFIRM_ANSWERS_MAX;
}

/**
 * Draft LOCAL de las respuestas, por reserva y POR USUARIO AUTENTICADO. El hub
 * no tiene canal de drafts y acá no se inventa uno: esto vive en la memoria de
 * la pestaña para que cerrar el modal sin querer no borre lo escrito.
 *
 * La identidad importa tanto como la reserva: el logout de la app es una
 * navegación del router, sin recargar la página, así que este módulo
 * SOBREVIVE al cambio de usuario. Sin scope, el que entra después vería —y
 * podría confirmar— las respuestas que escribió el anterior sobre la misma
 * reserva de la agencia. Por eso cada draft pertenece a un usuario, y cuando
 * la identidad cambia (o se va) se descarta TODO lo guardado: lo de antes ya
 * no es de nadie que esté mirando la pantalla.
 *
 * Las preguntas NO viven acá: esas son de la fila del booking y vuelven con
 * ella. Acá solo está lo que el asesor tipeó y todavía no mandó.
 */
const drafts = new Map<string, QuestionAnswersDraft>();

/** Tope de reservas con draft vivo: una sesión larga no crece sin fin. */
const MAX_DRAFTS = 20;

/** El usuario dueño de lo que hay guardado ahora mismo. */
let draftsOwner: string | null = null;

/**
 * Alinea la memoria con la identidad que está mirando la pantalla. Cualquier
 * cambio —otro usuario, o ninguno— vacía lo guardado antes de tocar nada.
 */
function withOwner(userId: string | undefined): string | null {
  const owner = userId ?? null;
  if (owner !== draftsOwner) {
    drafts.clear();
    draftsOwner = owner;
  }
  return owner;
}

export function readQuestionAnswersDraft(
  userId: string | undefined,
  bookingId: string | undefined,
): QuestionAnswersDraft {
  const owner = withOwner(userId);
  if (!owner || !bookingId) return {};
  return { ...(drafts.get(bookingId) ?? {}) };
}

export function saveQuestionAnswersDraft(
  userId: string | undefined,
  bookingId: string | undefined,
  answers: QuestionAnswersDraft,
): void {
  const owner = withOwner(userId);
  // Sin usuario identificado no se guarda nada: un draft sin dueño no tiene a
  // quién devolvérsele.
  if (!owner || !bookingId) return;
  // Reinsertar mueve la reserva al final: se descarta la MENOS usada.
  drafts.delete(bookingId);
  drafts.set(bookingId, { ...answers });
  while (drafts.size > MAX_DRAFTS) {
    const oldest = drafts.keys().next();
    if (oldest.done) break;
    drafts.delete(oldest.value);
  }
}

export function clearQuestionAnswersDraft(
  userId: string | undefined,
  bookingId: string | undefined,
): void {
  const owner = withOwner(userId);
  if (!owner || !bookingId) return;
  drafts.delete(bookingId);
}
